import React, { useEffect, useMemo, useState } from 'react';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { Button } from '@mui/joy';
import { toast } from 'react-toastify';
import { Backdrop, CircularProgress, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { getFetch, postFetch } from '../../../../../fetch/fetch';

const masterColumns = [
    { field: "customerPhoneDisplay", headerName: "고객번호",   width: 150 },
    {
        field: "lastCallDateTime", headerName: "마지막통화", width: 185,
        renderCell: (params: any) => (
            <span style={{ color: params.row.lastDirection === '↗' ? '#1976d2' : '#888' }}>
                {params.value}
            </span>
        )
    },
    { field: "totalCount",       headerName: "시도수",    width: 65 },
    { field: "answeredCount",    headerName: "정상응대",  width: 80 },
    { field: "missedCount",      headerName: "미응대",    width: 65 },
    { field: "totalSvcDuration", headerName: "총상담(초)", width: 90 },
    { field: "callbackStatus",   headerName: "콜백완료",  width: 110 },
    { field: "counselorName",    headerName: "상담원",    width: 100 },
    { field: "finalStatus",      headerName: "최종상태",  width: 100 },
];

const detailColumns = [
    { field: "time",          headerName: "일시",    width: 160 },
    {
        field: "callTypeDisplay", headerName: "종류", width: 110,
        renderCell: (params: any) => (
            <span style={{ color: params.row.callTypeRaw === 'O' ? '#1976d2' : '#888' }}>
                {params.value}
            </span>
        )
    },
    { field: "causeLabel",    headerName: "응대결과",   width: 200 },
    { field: "svcDuration",   headerName: "상담시간(초)", width: 100 },
    { field: "counselorName", headerName: "상담원",   width: 100 },
    { field: "sentiment",     headerName: "감정",     width: 80 },
    { field: "keyword",       headerName: "키워드",   width: 200 },
    {
        field: "hasRecording", headerName: "녹음", width: 60,
        renderCell: (params: any) => params.value ? <span>▶</span> : null
    },
];

const getCauseLabel = (r: any) => {
    const { cause, type, total_duration: td, message_count: mc } = r;
    if (type === 'O') {
        if (cause === 'Q') return '발신 응대';
        if (cause === '1') return `발신 부재중 (${td}초)`;
        return `발신 실패 (${td}초)`;
    }
    if (cause === 'Q') return '정상응대';
    if (cause === '1') return `부재중 (${td}초)`;
    if (mc > 0 && cause === '3') return `알림톡 자발선택 (${td}초)`;
    if (cause === '3') return `이탈 (${td}초)`;
    return cause || '';
};

const getCustomerPhone = (call: any) => call.ani;

const buildGroupedRows = (raw: any[], nameMap: Record<string, string>) => {
    const groups: Record<string, any[]> = {};
    for (const call of raw.filter(c => !c.message_count)) {
        const customerPhone = getCustomerPhone(call);
        const key = customerPhone || 'unknown';
        if (!groups[key]) groups[key] = [];
        groups[key].push(call);
    }

    return Object.entries(groups).map(([key, calls]) => {
        const sorted = [...calls].sort((a, b) =>
            (a.cs_date + a.cs_time).localeCompare(b.cs_date + b.cs_time));
        const first = sorted[0];
        const last = sorted[sorted.length - 1];

        const customerPhone = getCustomerPhone(first);
        const lastDirection = last.type === 'I' ? '↙' : '↗';
        const lastCallDateTime = last.cs_date && last.cs_time
            ? `${last.cs_date.slice(0, 4)}-${last.cs_date.slice(4, 6)}-${last.cs_date.slice(6, 8)} ${last.cs_time.slice(0, 2)}:${last.cs_time.slice(2, 4)} ${lastDirection}`
            : '';
        const _lastSortKey = (last.cs_date || '') + (last.cs_time || '');

        const answeredCount = calls.filter(c => c.cause === 'Q').length;
        const totalSvcDuration = calls.reduce((sum: number, c: any) => sum + (c.svc_duration || 0), 0);

        const missedCount = calls.filter(c =>
            (c.type === 'I' && c.cause === '1') ||
            (c.type === 'I' && c.cause === '3' && !c.message_count) ||
            (c.type === 'O' && c.cause === '1') ||
            (c.type === 'O' && c.cause === '3')
        ).length;

        let callbackStatus = '-';
        if (missedCount > 0) {
            if (last.cause === 'Q') {
                callbackStatus = '✅ 완료';
            } else if (last.type === 'O' && last.cause === '1') {
                callbackStatus = '⚠️ 재시도 필요';
            } else {
                callbackStatus = '❌ 미콜백';
            }
        }

        let finalStatus = '⚠️ 이탈';
        if (last.cause === 'Q') finalStatus = '✅ 응대';
        else if (last.type === 'O' && last.cause === '1') finalStatus = '📞 콜백시도';

        const counselorName = nameMap[last.cus_tel] || last.cus_tel || '';

        return {
            id: key,
            groupKey: key,
            customerPhone,
            customerPhoneDisplay: nameMap[customerPhone] || customerPhone,
            lastCallDateTime,
            lastDirection,
            _lastSortKey,
            totalCount: calls.length,
            answeredCount,
            missedCount,
            totalSvcDuration,
            callbackStatus,
            counselorName,
            finalStatus,
            _calls: sorted,
        };
    }).sort((a, b) => b._lastSortKey.localeCompare(a._lastSortKey));
};

const HappyTalkLogs: React.FC<any> = (props) => {

    const [rawCallLogs, setRawCallLogs] = useState<any[]>([]);
    const [phoneNameMap, setPhoneNameMap] = useState<Record<string, string>>({});
    const [callLogLoading, setCallLogLoading] = useState(false);
    const [callbackFilter, setCallbackFilter] = useState<'all' | 'uncallback' | 'retry' | 'escaped'>('all');
    const [phoneSearch, setPhoneSearch] = useState('');
    const [selectedGroupKey, setSelectedGroupKey] = useState<string | null>(null);

    const groupedRows = useMemo(
        () => buildGroupedRows(rawCallLogs, phoneNameMap),
        [rawCallLogs, phoneNameMap]
    );

    const filteredGroupedRows = useMemo(() => {
        let rows = groupedRows;
        if (callbackFilter === 'uncallback') rows = rows.filter(r => r.callbackStatus === '❌ 미콜백');
        if (callbackFilter === 'retry')      rows = rows.filter(r => r.callbackStatus === '⚠️ 재시도 필요');
        if (callbackFilter === 'escaped')    rows = rows.filter(r => r.finalStatus === '⚠️ 이탈');
        if (phoneSearch) rows = rows.filter(r => r.customerPhone.includes(phoneSearch));
        return rows;
    }, [groupedRows, callbackFilter, phoneSearch]);

    const detailRows = useMemo(() => {
        if (!selectedGroupKey) return [];
        const group = groupedRows.find(g => g.groupKey === selectedGroupKey);
        if (!group) return [];
        return [...group._calls].reverse().map((r: any, i: number) => ({
            id: i,
            time: r.cs_date && r.cs_time
                ? `${r.cs_date.slice(0, 4)}-${r.cs_date.slice(4, 6)}-${r.cs_date.slice(6, 8)} ${r.cs_time.slice(0, 2)}:${r.cs_time.slice(2, 4)}:${r.cs_time.slice(4, 6)}`
                : '',
            callTypeDisplay: r.type === 'I' ? '↙ 인바운드' : '↗ 아웃바운드',
            callTypeRaw: r.type,
            causeLabel: getCauseLabel(r),
            svcDuration: r.svc_duration,
            counselorName: phoneNameMap[r.cus_tel] || r.cus_tel || '',
            sentiment: r.sentiment || '',
            keyword: r.keyword || '',
            hasRecording: !!r.recfile_name,
        }));
    }, [selectedGroupKey, groupedRows, phoneNameMap]);

    const getCallLogs = async () => {
        try {
            const result: any = await getFetch("/happytalk/call-logs");
            if (result.message === "success") {
                setPhoneNameMap(result.phoneNameMap || {});
                setRawCallLogs(result.data);
            }
        } catch (e) {}
    };

    const refreshCallLogs = async () => {
        setCallLogLoading(true);
        try {
            const result: any = await postFetch("/happytalk/call-logs/refresh", {});
            if (result.message === "success") {
                setPhoneNameMap(result.phoneNameMap || {});
                setRawCallLogs(result.data);
                toast.success(`${result.count}건 동기화 완료!`, {
                    position: "bottom-right",
                    autoClose: 3000,
                    hideProgressBar: true,
                    theme: "colored"
                });
            }
        } catch (e) {
            toast.error("새로고침 실패!", {
                position: "bottom-right",
                autoClose: 3000,
                hideProgressBar: true,
                theme: "colored"
            });
        }
        setCallLogLoading(false);
    };

    useEffect(() => {
        getCallLogs();
    }, []);

    useEffect(() => {
        if (filteredGroupedRows.length > 0 && !selectedGroupKey) {
            setSelectedGroupKey(filteredGroupedRows[0].groupKey);
        }
    }, [filteredGroupedRows]);

    return (
        <div>
            <div style={{ marginBottom: "8px", display: "flex", alignItems: "center", gap: "12px" }}>
                <span style={{ fontWeight: "bold", fontSize: "15px" }}>HappyTalk 통화내역 (고객 단위)</span>
                <Button
                    color='primary'
                    variant='soft'
                    onClick={refreshCallLogs}
                    disabled={callLogLoading}
                >
                    새로고침
                </Button>
            </div>

            <div style={{ display: "flex", gap: "8px", marginBottom: "8px", alignItems: "center" }}>
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={callbackFilter}
                    onChange={(_: any, v: any) => {
                        if (v) {
                            setCallbackFilter(v);
                            setSelectedGroupKey(null);
                        }
                    }}
                >
                    <ToggleButton value="all">전체</ToggleButton>
                    <ToggleButton value="uncallback">❌ 미콜백만</ToggleButton>
                    <ToggleButton value="retry">⚠️ 재시도만</ToggleButton>
                    <ToggleButton value="escaped">이탈만</ToggleButton>
                </ToggleButtonGroup>
                <input
                    placeholder="고객번호 검색"
                    value={phoneSearch}
                    onChange={e => {
                        setPhoneSearch(e.target.value);
                        setSelectedGroupKey(null);
                    }}
                    style={{ padding: "4px 8px", border: "1px solid #ccc", borderRadius: "4px" }}
                />
            </div>

            <div style={{ display: "flex", width: "1700px", gap: "8px", marginBottom: "32px" }}>
                <div style={{ flex: "0 0 60%", height: "600px", backgroundColor: "white" }}>
                    <DataGridPremium
                        rows={filteredGroupedRows}
                        columns={masterColumns}
                        density='compact'
                        onRowClick={(params) => setSelectedGroupKey(params.row.groupKey)}
                        getRowClassName={(params) =>
                            params.row.groupKey === selectedGroupKey ? 'selected-group-row' : ''
                        }
                        sx={{ '& .selected-group-row': { backgroundColor: '#e3f2fd' } }}
                    />
                </div>
                <div style={{ flex: "0 0 calc(40% - 8px)", height: "600px", backgroundColor: "white" }}>
                    <DataGridPremium
                        rows={detailRows}
                        columns={detailColumns}
                        density='compact'
                    />
                </div>
            </div>

            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={callLogLoading}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
        </div>
    );
};

export default HappyTalkLogs;
